import { useState, useCallback } from 'react';
import { X, FileText, Camera } from 'lucide-react';
import { FileUpload } from '../FileUpload';
import { PhotoCapture } from '../PhotoCapture';
import { supabase } from '../../lib/supabase';

interface UploadDocumentModalProps {
  caseId: string;
  onClose: () => void;
  onDocumentUploaded: (ocrData?: any) => void;
}

export function UploadDocumentModal({ caseId, onClose, onDocumentUploaded }: UploadDocumentModalProps) {
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const handleClose = useCallback(() => {
    if (showPhotoCapture) {
      setShowPhotoCapture(false);
      // Small delay to allow camera cleanup
      const timer = setTimeout(() => {
        onClose();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      onClose();
    }
  }, [onClose, showPhotoCapture]);

  const handlePhotoData = async (data: any) => {
    try {
      setUploading(true);
      setError(null);
      
      // Create document record with OCR data
      const { error: uploadError } = await supabase
        .from('documents')
        .insert({
          case_id: caseId,
          type: 'other',
          filename: `capture-${Date.now()}.jpg`,
          ocr_data: data,
          metadata: {
            source: 'camera',
            timestamp: new Date().toISOString()
          }
        });

      if (uploadError) throw uploadError;
      
      // Show success message
      setSuccess('Document uploaded successfully! Go to Documents to view it.');
      
      // Pass OCR data back for auto-population
      onDocumentUploaded(data);
      
      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Failed to save document. Please try again.');
    } finally {
      setUploading(false);
      setShowPhotoCapture(false);
    }
  };

  const handleFileUploaded = async () => {
    try {
      setUploading(false);
      setSuccess('Document uploaded successfully! Go to Documents to view it.');
      
      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error handling upload:', err);
      setError('Failed to process document. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <FileText className="h-6 w-6 text-[#6563ff]" />
            <h2 className="text-2xl font-bold text-gray-800">Upload Documents</h2>
          </div>
          <button 
            onClick={handleClose}
            disabled={uploading}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-100 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        <div className="space-y-6">
          <button
            onClick={() => setShowPhotoCapture(true)}
            disabled={uploading}
            className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors flex items-center justify-center disabled:opacity-50"
          >
            <Camera className="h-5 w-5 mr-2" />
            Capture Document Photo
          </button>

          <div className="text-center relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative">
              <span className="px-3 text-sm text-gray-500 bg-white">or</span>
            </div>
          </div>

          <FileUpload
            caseId={caseId}
            onFileUploaded={() => handleFileUploaded()}
            disabled={uploading}
          />

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>

        {showPhotoCapture && (
          <PhotoCapture
            onDataExtracted={handlePhotoData}
            onClose={() => setShowPhotoCapture(false)}
          />
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6563ff] mb-4"></div>
              <p className="text-gray-600">Processing document...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}